import { useEffect, useRef, useState } from 'react'
import { Tooltip } from 'antd'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { useAppNavigate } from '../../hooks/useAppNavigate'
import { useBackToParent, usePersistentPageSize } from '@/hooks'
import { municipalitiesApi } from '@/services'
import { useToast } from '@/widgets/common'
import { formatDate } from '@/utils'
import useSearchPersistence from '@/hooks/useSearchPersistence'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'
import { Pagination, BackButton, SearchCard, ResizeHandle, SortableHeader, TableLoadingRow, DemoInput, DemoSelect, DemoBadge } from '@/widgets/demo/ListPrimitives';
import { ColumnVisibilityControl } from '@/widgets/demo/ColumnVisibilityControl';
import { demoButtonClass, computePagination, useResizableColumns, useSortState, type ColumnWidthMap, type SortOrder } from '@/widgets/demo/listHelpers';
const MUNICIPALITY_COLUMN_DEFAULTS: ColumnWidthMap = {
  select: 48,
  code: 168,
  name: 232,
  subdomain: 160,
  prefecture: 128,
  contact: 200,
  ocrUse: 96,
  status: 120,
  updatedAt: 136,
}

const MUNICIPALITY_COLUMN_ORDER = [
  'select',
  'code',
  'name',
  'subdomain',
  'prefecture',
  'contact',
  'ocrUse',
  'status',
  'updatedAt',
] as const

// 列表示設定（No.30 横展開）。選択列(select)と主キー相当の自治体コード(code)・自治体名(name)は
// 常に表示にして、行の同定（編集対象選択）ができなくなる事故を防ぐ。それ以外は切替可。
const MUNICIPALITY_COLUMN_VISIBILITY_KEY = 'municipality-management'
const MUNICIPALITY_ALWAYS_VISIBLE_COLUMNS = ['select', 'code', 'name']

// 列表示設定の列ラベル（一覧ヘッダの表示テキストと揃える）。
const MUNICIPALITY_COLUMN_LABELS: Record<(typeof MUNICIPALITY_COLUMN_ORDER)[number], string> = {
  select: '選択',
  code: '自治体コード',
  name: '自治体名',
  subdomain: 'サブドメイン',
  prefecture: '都道府県',
  contact: '窓口連絡先',
  ocrUse: 'OCR利用',
  status: 'ステータス',
  updatedAt: '最終更新日',
}

const MUNICIPALITY_COLUMN_VISIBILITY_ITEMS = MUNICIPALITY_COLUMN_ORDER.map((id) => ({
  id,
  label: MUNICIPALITY_COLUMN_LABELS[id],
}))

interface MunRow {
  code: string
  name: string
  subdomain: string
  prefecture: string
  contact: string
  status: string
  updatedAt: string
  ocrUse: string
  id: number
}

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'ステータス（全て）' },
  { value: 'ACTIVE', label: '有効' },
  { value: 'INACTIVE', label: '無効' },
]

const OCR_OPTIONS = [
  { value: 'ALL', label: 'OCR利用（全て）' },
  { value: 'ENABLED', label: '有効' },
  { value: 'DISABLED', label: '無効' },
]

// 検索条件は最後に適用した結果を保持する（顧客要望）。適用済みフィルタ・並び順・ページ番号を
// localStorage（search_<sub>_municipality-management）へ保存し、再訪問時に復元する。
interface MunicipalitySearchState {
  code: string
  name: string
  prefecture: string
  status: string
  ocrUse: string
  sortBy: string | null
  sortOrder: SortOrder
  page: number
}

const DEFAULT_MUNICIPALITY_SEARCH: MunicipalitySearchState = {
  code: '',
  name: '',
  prefecture: '',
  status: 'ALL',
  ocrUse: 'ALL',
  sortBy: null,
  sortOrder: null,
  page: 1,
}

const MunicipalityManagement = () => {
  const navigate = useAppNavigate()
  const goBackToParent = useBackToParent()
  const toast = useToast()
  const [savedSearch, setSavedSearch] = useSearchPersistence<MunicipalitySearchState>('municipality-management', DEFAULT_MUNICIPALITY_SEARCH)
  const [page, setPage] = useState(savedSearch.page ?? 1)
  const [pageSize, setPageSize] = usePersistentPageSize('municipality-management')
  const [rows, setRows] = useState<MunRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(() => new Set())
  const selectAllRef = useRef<HTMLInputElement>(null)
  const { sort, setSort, sortQuery } = useSortState({ sortBy: savedSearch.sortBy, order: savedSearch.sortOrder })
  const { widths: colWidths, startResize } = useResizableColumns({ defaults: MUNICIPALITY_COLUMN_DEFAULTS })

  // 列表示設定（No.30 横展開）。表示中の列だけに絞った順序配列を導出し、colgroup/thead/tbody/幅計算を統一する。
  const { isVisible: isColVisible, setVisible: setColVisible, reset: resetCols, hiddenCount: hiddenColCount } =
    useColumnVisibility(MUNICIPALITY_COLUMN_VISIBILITY_KEY, MUNICIPALITY_COLUMN_ORDER, {
      alwaysVisible: MUNICIPALITY_ALWAYS_VISIBLE_COLUMNS,
    })
  const visibleColumnOrder = MUNICIPALITY_COLUMN_ORDER.filter((id) => isColVisible(id))
  // 隠した列は colWidths から除外して合計幅を出す（隙間/幅ズレ防止）。
  const tableWidth = visibleColumnOrder.reduce((sum, id) => sum + (colWidths[id] ?? 0), 0)
  // tbody の空行/ローディング行 colSpan は「表示中の列数 + 末尾の余白 col(1)」。
  const visibleColSpan = visibleColumnOrder.length + 1
  const handleSortChange = (next: typeof sort) => {
    setSort(next)
    setPage(1)
  }

  const [qCode, setQCode] = useState(savedSearch.code)
  const [qName, setQName] = useState(savedSearch.name)
  const [qPrefecture, setQPrefecture] = useState(savedSearch.prefecture)
  const [qStatus, setQStatus] = useState(savedSearch.status)
  const [qOcrUse, setQOcrUse] = useState(savedSearch.ocrUse)
  const [applied, setApplied] = useState({
    code: savedSearch.code,
    name: savedSearch.name,
    prefecture: savedSearch.prefecture,
    status: savedSearch.status,
    ocrUse: savedSearch.ocrUse,
  })

  const { totalPages, safePage, rangeFrom, rangeTo } = computePagination(page, total, pageSize)
  const pageRows = rows

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setLoading(true)
      try {
        const { data } = await municipalitiesApi.search({
          code: applied.code.trim() || undefined,
          name: applied.name.trim() || undefined,
          prefecture: applied.prefecture.trim() || undefined,
          status: applied.status === 'ALL' ? undefined : applied.status,
          ocrUse: applied.ocrUse === 'ALL' ? undefined : applied.ocrUse === 'ENABLED',
          ...sortQuery,
          page: safePage - 1,
          size: pageSize,
        })
        if (cancelled) return
        setTotal(data.totalElements)
        setRows(
          data.content.map((m) => ({
            code: m.code,
            name: m.name,
            subdomain: m.subdomain || '',
            prefecture: m.prefecture || '',
            contact: m.contact || '',
            status: m.status,
            updatedAt: m.updatedAt ? formatDate(m.updatedAt) : '－',
            ocrUse: m.ocrUse ? '有' : '無',
            id: m.id,
          })),
        )
      } catch {
        if (!cancelled) {
          setRows([])
          setTotal(0)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [safePage, pageSize, applied, sortQuery])

  // 適用済みフィルタ・並び順・ページ番号を保存し、再訪問時に前回の検索結果を復元する。
  useEffect(() => {
    setSavedSearch({ ...applied, sortBy: sort.sortBy, sortOrder: sort.order, page })
  }, [applied, sort.sortBy, sort.order, page, setSavedSearch])

  const allPageSelected = pageRows.length > 0 && pageRows.every((r) => selectedCodes.has(r.code))
  const somePageSelected = pageRows.some((r) => selectedCodes.has(r.code))
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = somePageSelected && !allPageSelected
  }, [somePageSelected, allPageSelected])

  const toggleSelectAll = () => {
    const codes = pageRows.map((r) => r.code)
    setSelectedCodes((prev) => {
      const next = new Set(prev)
      if (allPageSelected) codes.forEach((c) => next.delete(c))
      else codes.forEach((c) => next.add(c))
      return next
    })
  }

  const toggleRow = (code: string) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  const runSearch = () => {
    setLoading(true)
    setPage(1)
    setApplied({
      code: qCode,
      name: qName,
      prefecture: qPrefecture,
      status: qStatus,
      ocrUse: qOcrUse,
    })
  }

  const runClear = () => {
    setLoading(true)
    setQCode('')
    setQName('')
    setQPrefecture('')
    setQStatus('ALL')
    setQOcrUse('ALL')
    setPage(1)
    setApplied({
      code: '',
      name: '',
      prefecture: '',
      status: 'ALL',
      ocrUse: 'ALL',
    })
  }

  const openMunicipalityEdit = () => {
    if (selectedCodes.size === 0) {
      toast.warning('自治体情報を編集するには、一覧で対象の1件にチェックを入れてください。')
      return
    }
    if (selectedCodes.size >= 2) {
      toast.warning('自治体情報を編集するには、1件のみチェックを入れてください。')
      return
    }
    navigate('municipality-edit', { code: [...selectedCodes][0] })
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[{ label: 'システム管理', page: 'submenu-admin' }, { label: '自治体管理' }]} />
      <div className="mx-auto w-full max-w-[1600px] px-6 py-8">
        <BackButton onClick={goBackToParent} />

        <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-gray-700">
          システムに連携する自治体の登録・編集を行います（ユーザ管理とは対象が異なります）。
        </div>

        <SearchCard className="mb-6">
          <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-5">
            <DemoInput type="text" value={qCode} onChange={(e) => setQCode(e.target.value)} placeholder="自治体コード（6桁）" />
            <DemoInput type="text" value={qName} onChange={(e) => setQName(e.target.value)} placeholder="自治体名" />
            <DemoInput type="text" value={qPrefecture} onChange={(e) => setQPrefecture(e.target.value)} placeholder="都道府県" />
            <DemoSelect value={qStatus} onChange={(e) => setQStatus(e.target.value)}>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </DemoSelect>
            <DemoSelect value={qOcrUse} onChange={(e) => setQOcrUse(e.target.value)}>
              {OCR_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </DemoSelect>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={runClear} className={demoButtonClass.secondary}>
              クリア
            </button>
            <button type="button" onClick={runSearch} className={demoButtonClass.primary}>
              検索
            </button>
          </div>
        </SearchCard>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => navigate('municipality-create')} className={demoButtonClass.primarySm}>
            新規自治体登録
          </button>
          <button type="button" onClick={openMunicipalityEdit} className={demoButtonClass.outline}>
            自治体情報編集
          </button>
          <div className="ml-auto">
            <ColumnVisibilityControl
              columns={MUNICIPALITY_COLUMN_VISIBILITY_ITEMS}
              isVisible={isColVisible}
              setVisible={setColVisible}
              onReset={resetCols}
              alwaysVisible={MUNICIPALITY_ALWAYS_VISIBLE_COLUMNS}
              hiddenCount={hiddenColCount}
            />
          </div>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed" style={{ width: `${tableWidth}px` }}>
              <colgroup>
                {visibleColumnOrder.map((id) => (
                  <col key={id} style={{ width: `${colWidths[id]}px` }} />
                ))}
                <col />
              </colgroup>
              <thead className="border-b-2 border-gray-200 bg-gray-50">
                <tr>
                  {isColVisible('select') && (
                    <th className="px-2 py-3 text-center">
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                  )}
                  {isColVisible('code') && (
                    <SortableHeader field="code" label="自治体コード" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal whitespace-nowrap" trailing={<ResizeHandle columnId="code" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('name') && (
                    <SortableHeader field="name" label="自治体名" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal whitespace-nowrap" trailing={<ResizeHandle columnId="name" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('subdomain') && (
                    <SortableHeader field="subdomain" label="サブドメイン" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal whitespace-nowrap" trailing={<ResizeHandle columnId="subdomain" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('prefecture') && (
                    <SortableHeader field="prefecture" label="都道府県" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal whitespace-nowrap" trailing={<ResizeHandle columnId="prefecture" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('contact') && (
                    <th className="relative px-2 py-3 text-left text-sm font-medium text-gray-700 whitespace-nowrap">
                      窓口連絡先
                      <ResizeHandle columnId="contact" onMouseDown={startResize} />
                    </th>
                  )}
                  {isColVisible('ocrUse') && (
                    <th className="relative px-2 py-3 text-left text-sm font-medium text-gray-700 whitespace-nowrap">
                      OCR利用
                      <ResizeHandle columnId="ocrUse" onMouseDown={startResize} />
                    </th>
                  )}
                  {isColVisible('status') && (
                    <SortableHeader field="status" label="ステータス" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal whitespace-nowrap" trailing={<ResizeHandle columnId="status" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('updatedAt') && (
                    <th className="px-2 py-3 text-left text-sm font-medium text-gray-700 whitespace-nowrap">最終更新日</th>
                  )}
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody className={`divide-y divide-gray-200 transition-opacity ${loading ? 'opacity-60' : ''}`}>
                {pageRows.length === 0 ? (
                  loading ? (
                    <TableLoadingRow colSpan={visibleColSpan} />
                  ) : (
                    <tr>
                      <td colSpan={visibleColSpan} className="px-4 py-8 text-center text-sm text-gray-500">
                        該当するデータがありません
                      </td>
                    </tr>
                  )
                ) : (
                  pageRows.map((r) => (
                    <tr key={r.code} className="transition hover:bg-blue-50">
                        {isColVisible('select') && (
                          <td className="px-2 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedCodes.has(r.code)}
                              onChange={() => toggleRow(r.code)}
                              className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                        )}
                        {isColVisible('code') && (
                          <td className="px-2 py-3 text-sm">
                            <Tooltip title={r.code} placement="topLeft">
                              <span className="block truncate">{r.code}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('name') && (
                          <td className="px-2 py-3 text-sm">
                            <Tooltip title={r.name} placement="topLeft">
                              <span className="block truncate">{r.name}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('subdomain') && (
                          <td className="px-2 py-3 text-sm">
                            <Tooltip title={r.subdomain || '—'} placement="topLeft">
                              <span className="block truncate" style={{ fontFamily: 'monospace' }}>{r.subdomain || '—'}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('prefecture') && (
                          <td className="px-2 py-3 text-sm">
                            <Tooltip title={r.prefecture} placement="topLeft">
                              <span className="block truncate">{r.prefecture}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('contact') && (
                          <td className="px-2 py-3 text-sm">
                            <Tooltip title={r.contact} placement="topLeft">
                              <span className="block truncate">{r.contact}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('ocrUse') && (
                          <td className="px-2 py-3 truncate">
                            <span
                              className={`rounded-full px-2 py-1 text-xs ${
                                r.ocrUse === '有' ? 'bg-violet-100 text-violet-800' : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {r.ocrUse}
                            </span>
                          </td>
                        )}
                        {isColVisible('status') && (
                          <td className="px-2 py-3 truncate">
                            <DemoBadge status={r.status} />
                          </td>
                        )}
                        {isColVisible('updatedAt') && (
                          <td className="px-2 py-3 text-sm">
                            <Tooltip title={r.updatedAt} placement="topLeft">
                              <span className="block truncate">{r.updatedAt}</span>
                            </Tooltip>
                          </td>
                        )}
                      <td aria-hidden="true" />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            total={total}
            rangeFrom={rangeFrom}
            rangeTo={rangeTo}
            onPageChange={(next) => { setLoading(true); setPage(next); }}
            pageSize={pageSize}
            onPageSizeChange={(next) => { setLoading(true); setPageSize(next); setPage(1); }}
          />
        </div>
      </div>
    </div>
  )
}

export default MunicipalityManagement
